<template>
    <div>
        <Dialog v-model:visible="visible" :style="{ width: '450px' }" header="Create a New License" :modal="true" :dismissableMask="true" class="p-fluid dialog-header" @hide="emit('hide-dialog', true)">
            <div class="field">
                <label for="companyId" class="mb-3">Company</label>
                <Dropdown
                    id="companyId"
                    aria-label="companyId"
                    v-model="license.company_id"
                    :options="companies"
                    optionLabel="name"
                    optionValue="id"
                    placeholder="Select a Company"
                    :class="{ 'p-invalid': submitted && formErrors.companyId }"
                    showClear
                />
                <small class="p-invalid" v-if="submitted && formErrors.companyId">{{ formErrors.companyId }}</small>
            </div>
            <div class="field">
                <label for="productId" class="mb-3">Product</label>
                <Dropdown
                    id="productId"
                    aria-label="productId"
                    v-model="license.product_id"
                    :options="products"
                    optionLabel="code"
                    optionValue="id"
                    placeholder="Select a Product"
                    :class="{ 'p-invalid': submitted && formErrors.productId }"
                    showClear
                />
                <small class="p-invalid" v-if="submitted && formErrors.productId">{{ formErrors.productId }}</small>
            </div>
            <div class="field">
                <label for="startDate" class="mb-3">Contract Start Date</label>
                <Calendar id="startDate" aria-label="startDate" :showIcon="true" :showButtonBar="true" v-model="license.start_date" dateFormat="dd-mm-yy" :class="{ 'p-invalid': submitted && formErrors.startDate }" />
                <small class="p-invalid" v-if="submitted && formErrors.startDate">{{ formErrors.startDate }}</small>
            </div>
            <div class="field">
                <label for="contractPeriod" class="mb-3">Contract Term / Period</label>
                <Dropdown id="contractPeriod" aria-label="contractPeriod" v-model="contractPeriod" :options="periods" optionLabel="label" optionValue="value" placeholder="Select a term" />
            </div>
            <div class="field">
                <label for="endDate" class="mb-3">Contract End Date</label>
                <Calendar id="endDate" aria-label="endDate" :showIcon="true" :showButtonBar="true" v-model="license.end_date" dateFormat="dd-mm-yy" disabled />
                <small class="p-invalid" v-if="submitted && formErrors.endDate">{{ formErrors.endDate }}</small>
            </div>
            <div class="field">
                <label for="autoRenew" class="mb-3">Auto Renewal Period</label>
                <Dropdown id="autoRenew" aria-label="autoRenew" v-model="license.auto_renew" :options="periods" optionLabel="label" optionValue="value" placeholder="Select Renewal Period" />
                <small class="p-invalid" v-if="submitted && formErrors.autoRenew">{{ formErrors.autoRenew }}</small>
            </div>
            <template #footer>
                <Button label="Cancel" icon="pi pi-times" class="p-button-danger" @click="visible = false" />
                <Button label="Create" icon="pi pi-check" class="ml-2 p-button-primary" @click="submit" />
            </template>
        </Dialog>
    </div>
</template>

<script setup>
// import LicenseStartDate from './LicenseStartDate.vue';

import { storeToRefs } from 'pinia';
import { ref, computed, onMounted, watch } from 'vue';
import { object, string, number, array } from 'yup';

import { licenseStore, companyStore, productStore } from '@/store/global-stores';

import { addMonthsToDate, localeToISODate } from '@/utilities/date-formatter';
import { handleValidationErrors } from '@/utilities/form-errors-handler';

const emit = defineEmits(['hide-dialog']);
// extracting store states
const { license, periods } = storeToRefs(licenseStore);
const { companies } = storeToRefs(companyStore);
const { products } = storeToRefs(productStore);

const contractPeriod = ref(null);
const visible = ref(true);
const submitted = ref(false);
const formErrors = ref([]);

const validationSchema = object({
    company_id: number().required('Company selection is required'),
    product_id: number().required('Product selection is required'),
    start_date: string().required('Contract Start Date is required'),
    end_date: string().required('Contract End Date is required'),
    auto_renew: string().required('Auto Renewal Period is mandatory'),
    notified_users: array().nullable(),
});

const startDate = computed(() => {
    return license.value.start_date;
});

// Watch for start date and contract period value changes and if both are selected then generate contract end date
watch([startDate, contractPeriod], () => {
    if ((startDate.value, contractPeriod.value)) {
        license.value.end_date = addMonthsToDate(startDate.value, contractPeriod.value);
    }
});

onMounted(async () => {
    await productStore.getAllProducts();
});

const submit = async () => {
    try {
        submitted.value = true;
        // validate the schema first
        await validationSchema.validate(license.value, { abortEarly: false });
        // if valid then submit the license form
        if (await licenseStore.createLicense({ ...license.value, end_date: localeToISODate(license.value.end_date) })) {
            emit('hide-dialog');
        }
    } catch (error) {
        if (error.inner && typeof error.inner === 'object') {
            formErrors.value = handleValidationErrors(error);
        }
    }
};
</script>
